export type PortalConversationTone = 'blue' | 'green' | 'violet' | 'orange' | 'sky' | 'amber';

export type PortalConversationFilterChip = 'all' | 'unread' | 'mine' | 'mentioned';

export type PortalConversationParticipant = {
  id: string;
  name: string;
  role: string;
};

export type PortalConversationAttachment = {
  id: string;
  name: string;
  sizeLabel: string;
  dateLabel: string;
  downloadUrl: string;
};

export type PortalConversationLink = {
  id: string;
  kindLabel: string;
  title: string;
  href: string;
};

export type PortalConversationMessage = {
  id: string;
  authorName: string;
  body: string;
  whenLabel: string;
  isMine?: boolean;
  mentions?: string[];
  reaction?: string;
  attachment?: PortalConversationAttachment;
};

export type PortalConversationItem = {
  id: string;
  title: string;
  subject: string;
  preview: string;
  whenLabel: string;
  unreadCount: number;
  projectId: string;
  projectName: string;
  tone: PortalConversationTone;
  statusLabel: string;
  createdAtLabel: string;
  notificationsEnabled: boolean;
  isMine: boolean;
  mentionedMe: boolean;
  participants: PortalConversationParticipant[];
  messages: PortalConversationMessage[];
  files: PortalConversationAttachment[];
  links: PortalConversationLink[];
};

export type PortalConversationsData = {
  projects: { id: string; name: string }[];
  conversations: PortalConversationItem[];
};

const downloadUrl = 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf';

export const portalConversationsMock: PortalConversationsData = {
  projects: [
    { id: 'crm', name: 'Implementação CRM' },
    { id: 'dashboard-comercial', name: 'Dashboard Comercial' },
    { id: 'integracao-erp', name: 'Integração ERP' },
    { id: 'portal-cliente', name: 'Portal do Cliente' },
  ],
  conversations: [
    {
      id: 'conv-erp',
      title: 'Integração com ERP',
      subject: 'Discussão sobre a integração do CRM com o ERP financeiro',
      preview: 'Rafael: @AnaSouza enviei o mapeamento dos campos...',
      whenLabel: '10:24',
      unreadCount: 2,
      projectId: 'crm',
      projectName: 'Implementação CRM',
      tone: 'blue',
      statusLabel: 'Ativa',
      createdAtLabel: '12/10/2026',
      notificationsEnabled: true,
      isMine: true,
      mentionedMe: true,
      participants: [
        { id: 'p1', name: 'Ana Souza', role: 'Patrocinadora' },
        { id: 'p2', name: 'Rafael Oliveira', role: 'Gerente do projeto' },
        { id: 'p3', name: 'Carlos Mendes', role: 'Analista de integração' },
        { id: 'p4', name: 'Mariana Silva', role: 'Consultora' },
        { id: 'p5', name: 'Bruno Costa', role: 'Analista comercial' },
      ],
      messages: [
        {
          id: 'm1',
          authorName: 'Rafael Oliveira',
          body: 'Bom dia! Atualizei o mapeamento dos campos da integração com o ERP.',
          whenLabel: '09:12',
        },
        {
          id: 'm2',
          authorName: 'Carlos Mendes',
          body: 'Segue a planilha com os endpoints prioritários para a primeira onda.',
          whenLabel: '09:28',
          attachment: {
            id: 'att-xlsx',
            name: 'mapeamento_campos_erp.xlsx',
            sizeLabel: '186 KB',
            dateLabel: '28/10/2026',
            downloadUrl,
          },
        },
        {
          id: 'm3',
          authorName: 'Ana Souza',
          body: 'Obrigada! @RafaelOliveira consegue validar o saldo por depósito nesta semana?',
          whenLabel: '09:45',
          isMine: true,
          mentions: ['RafaelOliveira'],
          reaction: '👍 2',
        },
        {
          id: 'm4',
          authorName: 'Rafael Oliveira',
          body: '@AnaSouza enviei o mapeamento dos campos e a janela de homologação proposta.',
          whenLabel: '10:24',
          mentions: ['AnaSouza'],
        },
      ],
      files: [
        {
          id: 'f1',
          name: 'mapeamento_campos_erp.xlsx',
          sizeLabel: '186 KB',
          dateLabel: '28/10/2026',
          downloadUrl,
        },
        {
          id: 'f2',
          name: 'log_homologacao.txt',
          sizeLabel: '42 KB',
          dateLabel: '27/10/2026',
          downloadUrl,
        },
        {
          id: 'f3',
          name: 'contrato_integracao.pdf',
          sizeLabel: '1.1 MB',
          dateLabel: '20/10/2026',
          downloadUrl,
        },
        {
          id: 'f4',
          name: 'fluxo_sincronizacao.png',
          sizeLabel: '820 KB',
          dateLabel: '18/10/2026',
          downloadUrl,
        },
      ],
      links: [
        {
          id: 'l1',
          kindLabel: 'Atividade',
          title: 'Integração de estoque',
          href: '/portal/projects/crm?tab=activities',
        },
        {
          id: 'l2',
          kindLabel: 'Entrega',
          title: 'Conector ERP v1',
          href: '/portal/projects/crm?tab=deliveries',
        },
        {
          id: 'l3',
          kindLabel: 'Documento',
          title: 'Especificação de integração',
          href: '/portal/projects/crm?tab=files',
        },
        {
          id: 'l4',
          kindLabel: 'Risco',
          title: 'Atraso na liberação da API',
          href: '/portal/projects/crm?tab=risks-changes',
        },
        {
          id: 'l5',
          kindLabel: 'Solicitação',
          title: 'Ampliar escopo da integração',
          href: '/portal/requests',
        },
      ],
    },
    {
      id: 'conv-dashboard',
      title: 'Dashboard comercial',
      subject: 'Indicadores e layout do painel gerencial',
      preview: 'Bruno: Precisamos do KPI de conversão por etapa.',
      whenLabel: 'Ontem',
      unreadCount: 1,
      projectId: 'dashboard-comercial',
      projectName: 'Dashboard Comercial',
      tone: 'green',
      statusLabel: 'Ativa',
      createdAtLabel: '08/10/2026',
      notificationsEnabled: true,
      isMine: false,
      mentionedMe: false,
      participants: [
        { id: 'p1', name: 'Bruno Costa', role: 'Analista comercial' },
        { id: 'p2', name: 'Rafael Oliveira', role: 'Gerente do projeto' },
        { id: 'p3', name: 'Ana Souza', role: 'Patrocinadora' },
      ],
      messages: [
        {
          id: 'm1',
          authorName: 'Bruno Costa',
          body: 'Precisamos do KPI de conversão por etapa no painel gerencial.',
          whenLabel: 'Ontem, 16:40',
        },
        {
          id: 'm2',
          authorName: 'Rafael Oliveira',
          body: 'Vamos alinhar com a solicitação #015 antes de priorizar no backlog.',
          whenLabel: 'Ontem, 17:05',
        },
      ],
      files: [
        {
          id: 'f1',
          name: 'mockup_dashboard.png',
          sizeLabel: '1.2 MB',
          dateLabel: '22/10/2026',
          downloadUrl,
        },
      ],
      links: [
        {
          id: 'l1',
          kindLabel: 'Solicitação',
          title: 'Incluir novo indicador no dashboard',
          href: '/portal/requests',
        },
      ],
    },
    {
      id: 'conv-training',
      title: 'Capacitação da equipe',
      subject: 'Agenda das turmas de treinamento do CRM',
      preview: 'Mariana: Turma extra disponível na quinta.',
      whenLabel: 'Ontem',
      unreadCount: 0,
      projectId: 'crm',
      projectName: 'Implementação CRM',
      tone: 'orange',
      statusLabel: 'Ativa',
      createdAtLabel: '05/10/2026',
      notificationsEnabled: false,
      isMine: true,
      mentionedMe: false,
      participants: [
        { id: 'p1', name: 'Mariana Silva', role: 'Consultora' },
        { id: 'p2', name: 'Ana Souza', role: 'Patrocinadora' },
      ],
      messages: [
        {
          id: 'm1',
          authorName: 'Mariana Silva',
          body: 'Turma extra disponível na quinta-feira às 14h.',
          whenLabel: 'Ontem, 11:20',
        },
      ],
      files: [],
      links: [
        {
          id: 'l1',
          kindLabel: 'Atividade',
          title: 'Treinamento da equipe comercial',
          href: '/portal/projects/crm?tab=activities',
        },
      ],
    },
    {
      id: 'conv-portal',
      title: 'Permissões do portal',
      subject: 'Dúvidas sobre acesso a documentos e papéis',
      preview: 'Fernanda: Conseguimos restringir contratos aos admins?',
      whenLabel: '24/10',
      unreadCount: 0,
      projectId: 'portal-cliente',
      projectName: 'Portal do Cliente',
      tone: 'violet',
      statusLabel: 'Ativa',
      createdAtLabel: '02/10/2026',
      notificationsEnabled: true,
      isMine: false,
      mentionedMe: true,
      participants: [
        { id: 'p1', name: 'Fernanda Lima', role: 'Administradora' },
        { id: 'p2', name: 'Mariana Silva', role: 'Consultora' },
        { id: 'p3', name: 'Ana Souza', role: 'Patrocinadora' },
      ],
      messages: [
        {
          id: 'm1',
          authorName: 'Fernanda Lima',
          body: 'Conseguimos restringir contratos apenas aos administradores?',
          whenLabel: '24/10, 09:15',
        },
        {
          id: 'm2',
          authorName: 'Mariana Silva',
          body: '@AnaSouza sim, pela matriz de permissões em Minha Conta.',
          whenLabel: '24/10, 10:02',
          mentions: ['AnaSouza'],
        },
      ],
      files: [
        {
          id: 'f1',
          name: 'matriz_permissoes.pdf',
          sizeLabel: '320 KB',
          dateLabel: '24/10/2026',
          downloadUrl,
        },
      ],
      links: [
        {
          id: 'l1',
          kindLabel: 'Solicitação',
          title: 'Dúvida sobre permissões do portal',
          href: '/portal/requests',
        },
      ],
    },
    {
      id: 'conv-homolog',
      title: 'Homologação financeira',
      subject: 'Checklist e evidências da homologação',
      preview: 'Carlos: Evidências anexadas no ambiente de testes.',
      whenLabel: '22/10',
      unreadCount: 0,
      projectId: 'integracao-erp',
      projectName: 'Integração ERP',
      tone: 'sky',
      statusLabel: 'Ativa',
      createdAtLabel: '28/09/2026',
      notificationsEnabled: true,
      isMine: false,
      mentionedMe: false,
      participants: [
        { id: 'p1', name: 'Carlos Mendes', role: 'Analista de integração' },
        { id: 'p2', name: 'João Santos', role: 'Patrocinador' },
        { id: 'p3', name: 'Rafael Oliveira', role: 'Gerente do projeto' },
      ],
      messages: [
        {
          id: 'm1',
          authorName: 'Carlos Mendes',
          body: 'Evidências anexadas no ambiente de testes para revisão.',
          whenLabel: '22/10, 15:30',
        },
      ],
      files: [],
      links: [
        {
          id: 'l1',
          kindLabel: 'Entrega',
          title: 'Homologação do módulo financeiro',
          href: '/portal/projects/crm?tab=deliveries',
        },
      ],
    },
    {
      id: 'conv-go-live',
      title: 'Janela de go-live',
      subject: 'Alinhamento da data de entrada em produção',
      preview: 'João: Preferimos adiar em uma semana.',
      whenLabel: '20/10',
      unreadCount: 0,
      projectId: 'integracao-erp',
      projectName: 'Integração ERP',
      tone: 'amber',
      statusLabel: 'Ativa',
      createdAtLabel: '15/09/2026',
      notificationsEnabled: true,
      isMine: true,
      mentionedMe: false,
      participants: [
        { id: 'p1', name: 'João Santos', role: 'Patrocinador' },
        { id: 'p2', name: 'Ana Souza', role: 'Patrocinadora' },
        { id: 'p3', name: 'Rafael Oliveira', role: 'Gerente do projeto' },
      ],
      messages: [
        {
          id: 'm1',
          authorName: 'João Santos',
          body: 'Preferimos adiar o go-live em uma semana por causa da campanha.',
          whenLabel: '20/10, 14:10',
        },
      ],
      files: [],
      links: [
        {
          id: 'l1',
          kindLabel: 'Solicitação',
          title: 'Adiar go-live em 1 semana',
          href: '/portal/requests',
        },
      ],
    },
  ],
};
